import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
  TextInput,
  Platform,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { colors, spacing, typography } from '../theme';
import { Ionicons } from '@expo/vector-icons';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth } = Dimensions.get('window');

interface EmojiPickerProps {
  visible: boolean;
  onEmojiSelect: (emoji: string) => void;
  onClose: () => void;
  horizontalInset?: number;
}

interface EmojiCategory {
  name: string;
  icon: keyof typeof Ionicons.glyphMap;
  emojis: string[];
  keywords: string[];
}

const BASE_EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    name: 'Faces',
    icon: 'happy-outline',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😔', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥'],
    keywords: ['face', 'emotion', 'reaction', 'smile', 'sad', 'happy', 'angry', 'cry', 'feeling']
  },
  {
    name: 'Hearts',
    icon: 'heart-outline',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '♥️', '💯', '💢', '💥', '💫', '💦', '💨', '🕳️', '💣', '💤', '💢', '💥', '💫', '💦', '💨', '🕳️', '💣', '💤'],
    keywords: ['heart', 'love', 'romance', 'affection', 'valentine']
  },
  {
    name: 'Gestures',
    icon: 'hand-left-outline',
    emojis: ['👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '🤝', '👏', '🙌', '👐', '🤲', '🤜', '🤛', '✊', '👊', '👎', '👍', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '🤝', '👏', '🙌', '👐', '🤲', '🤜', '🤛', '✊', '👊'],
    keywords: ['hand', 'gesture', 'sign', 'thumbs', 'wave']
  },
  {
    name: 'Objects',
    icon: 'cube-outline',
    emojis: ['📱', '💻', '🖥', '⌨️', '🖱', '🖨', '📷', '📹', '🎥', '📽', '🎞', '📞', '☎️', '📟', '📠', '📺', '📻', '🎙', '🎚', '🎛', '🧭', '⏱', '⏲', '⏰', '🕰', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯', '🪔', '🧯', '🛢', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🧰', '🔧', '🔨', '⚒', '🛠', '⛏', '🔩', '⚙️', '🧱', '⛓', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡', '⚔️', '🛡', '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🕳', '🩹', '🩺', '💊', '💉', '🧬', '🦠', '🧫', '🧪', '🌡', '🧹', '🧺', '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🧴', '🧷', '🧸', '🧵', '🧶', '🪡', '🪢', '🪣', '🧽', '🧯', '🛒'],
    keywords: ['object', 'tool', 'device', 'money', 'tech', 'gear']
  },
  {
    name: 'Nature',
    icon: 'leaf-outline',
    emojis: ['🌱', '🌿', '🍀', '🌾', '🌵', '🌲', '🌳', '🌴', '🌰', '🌍', '🌎', '🌏', '🌐', '🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘', '🌙', '🌚', '🌛', '🌜', '🌝', '🌞', '⭐', '🌟', '💫', '✨', '☄️', '☀️', '🌤', '⛅', '🌥', '☁️', '🌦', '🌧', '⛈', '🌩', '🌨', '❄️', '☃️', '⛄', '🌬', '💨', '💧', '💦', '☔', '☂️', '🌊', '🌫', '🌪', '🔥', '💥', '⚡', '🌟', '✨', '💫', '⭐', '🌠', '🌌'],
    keywords: ['nature', 'weather', 'plant', 'animal', 'earth', 'sky']
  }
];

const ALL_CATEGORY: EmojiCategory = {
  name: 'All',
  icon: 'grid-outline',
  emojis: Array.from(
    new Set(
      BASE_EMOJI_CATEGORIES.flatMap((category) => category.emojis)
    )
  ),
  keywords: ['all', 'everything', 'any', 'emoji', 'emojis']
};

const EMOJI_CATEGORIES: EmojiCategory[] = [ALL_CATEGORY, ...BASE_EMOJI_CATEGORIES];

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  visible,
  onEmojiSelect,
  onClose,
  horizontalInset = 0,
}) => {
  const styles = useThemedStyles(getStyles);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [categorySearch, setCategorySearch] = useState('');
  const isWeb = Platform.OS === 'web';
  const effectiveInset = isWeb ? Math.max(horizontalInset, 0) : 0;
  const containerWidth = Math.max(screenWidth - effectiveInset * 2, 320);
  const emojiButtonWidth = Math.max((containerWidth - spacing.md * 2) / 8, 32);

  const filteredCategories = useMemo(() => {
    const trimmed = categorySearch.trim().toLowerCase();
    if (!trimmed) return EMOJI_CATEGORIES;

    return EMOJI_CATEGORIES.filter((category) => {
      const nameMatch = category.name.toLowerCase().includes(trimmed);
      const keywordMatch = category.keywords.some((keyword) =>
        keyword.toLowerCase().includes(trimmed)
      );
      return nameMatch || keywordMatch;
    });
  }, [categorySearch]);

  const safeSelectedCategory = filteredCategories.length > 0
    ? Math.min(selectedCategory, filteredCategories.length - 1)
    : 0;

  const handleEmojiPress = (emoji: string) => {
    onEmojiSelect(emoji);
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={[
            styles.container,
            effectiveInset > 0 && {
              marginLeft: effectiveInset,
              marginRight: effectiveInset,
              width: containerWidth,
              alignSelf: 'center',
            },
          ]}
        >
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Choose Emoji</Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Ionicons name="close" size={24} color={colors.textDark} />
            </TouchableOpacity>
          </View>

          <View style={styles.searchContainer}>
            <Ionicons
              name="search"
              size={16}
              color={colors.text.secondary}
              style={styles.searchIcon}
            />
            <TextInput
              style={styles.searchInput}
              placeholder="Search categories..."
              placeholderTextColor={colors.text.secondary}
              value={categorySearch}
              onChangeText={(text) => {
                setCategorySearch(text);
                setSelectedCategory(0);
              }}
            />
          </View>

          {filteredCategories.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="search-outline" size={28} color={colors.text.secondary} />
              <Text style={styles.emptyStateTitle}>No categories found</Text>
              <Text style={styles.emptyStateSubtitle}>
                Try another keyword like “faces”, “hearts”, or “gestures”.
              </Text>
            </View>
          ) : (
            <>
              {/* Category Tabs */}
              <View style={styles.categoryTabs}>
                {filteredCategories.map((category, index) => (
                  <TouchableOpacity
                    key={category.name}
                    style={[
                      styles.categoryTab,
                      safeSelectedCategory === index && styles.categoryTabActive
                    ]}
                    onPress={() => setSelectedCategory(index)}
                  >
                    <Ionicons
                      name={category.icon as any}
                      size={20}
                      color={safeSelectedCategory === index ? colors.primary : colors.textLight}
                    />
                  </TouchableOpacity>
                ))}
              </View>

              {/* Emoji Grid */}
              <ScrollView style={styles.emojiContainer} showsVerticalScrollIndicator={false}>
                <View style={styles.emojiGrid}>
                  {filteredCategories[safeSelectedCategory]?.emojis.map((emoji, index) => (
                    <TouchableOpacity
                      key={index}
                      style={[styles.emojiButton, { width: emojiButtonWidth }]}
                      onPress={() => handleEmojiPress(emoji)}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.emoji}>{emoji}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  container: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    minHeight: '50%',
    // Enhanced shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 12,
    backgroundColor: colors.background.secondary,
  },
  searchIcon: {
    marginRight: spacing.xs,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  categoryTabs: {
    flexDirection: 'row',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  categoryTab: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderRadius: 8,
    marginHorizontal: 2,
  },
  categoryTabActive: {
    backgroundColor: colors.primaryTransparent,
  },
  emojiContainer: {
    flex: 1,
    paddingHorizontal: spacing.md,
  },
  emojiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingVertical: spacing.sm,
  },
  emojiButton: {
    width: (screenWidth - spacing.md * 2) / 8,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    marginVertical: 2,
  },
  emoji: {
    fontSize: 24,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  emptyStateTitle: {
    fontSize: typography.fontSize.md,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  emptyStateSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
    paddingHorizontal: spacing.xl,
  },
});
